import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ParkingSlotManager.css';
import { getApiUrl } from '../utils/api';

/**
 * Gets the ID token from MSAL instance
 * @param {Object} msalInstance - MSAL instance
 * @returns {Promise<string|null>} - ID token or null if unavailable
 */
async function getIdToken(msalInstance) {
  try {
    // Get all accounts and use the first one
    const accounts = msalInstance.getAllAccounts();
    if (!accounts || accounts.length === 0) {
      return null;
    }
    const account = accounts[0];
    // console.log('Getting ID token for account:', account);
    // Try to get token silently
    const response = await msalInstance.acquireTokenSilent({
      scopes: ['User.Read'],
      account: account,
    });
    // console.log('ID token response:', response);
    return response.idToken || null;
  } catch (error) {
    console.error('Failed to get ID token:', error);
    return null;
  }
}

function ParkingSlotManager({ user, powerUserMode, msalInstance }) {
  const { t } = useTranslation();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingSlot, setEditingSlot] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    label: '',
    location: 'basement',
    ev_charging: false,
    default_owner: '',
    available: true,
    display_order: '',
  });

  // Check if user has power user mode enabled
  const canManageSlots = user?.isPowerUser && powerUserMode;

  // Load parking slots from API
  useEffect(() => {
    loadSlots();
  }, []);

  const loadSlots = async () => {
    try {
      setLoading(true);
      setError(null);
      const idToken = await getIdToken(msalInstance);
      if (!idToken) {
        throw new Error(t('auth.failedToGetToken'));
      }

      const response = await fetch(getApiUrl('/api/parkingSlots'), {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load parking slots: ${response.statusText}`);
      }

      const data = await response.json();
      setSlots(data);
    } catch (e) {
      console.error('Failed to load parking slots:', e);
      setError(t('slotManager.failedToLoadSlots'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setIsCreating(true);
    setEditingSlot(null);
    setFormData({
      label: '',
      location: 'basement',
      ev_charging: false,
      default_owner: '',
      available: true,
      display_order: slots.length + 1,
    });
  };

  const handleEdit = (slot) => {
    setEditingSlot(slot);
    setIsCreating(false);
    setFormData({
      label: slot.label,
      location: slot.location || 'basement',
      ev_charging: slot.ev_charging === 1,
      default_owner: slot.default_owner || '',
      available: slot.available === 1,
      display_order: slot.display_order,
    });
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingSlot(null);
    setFormData({
      label: '',
      location: 'basement',
      ev_charging: false,
      default_owner: '',
      available: true,
      display_order: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canManageSlots) {
      alert('You must be a PowerUser with PowerUser mode enabled to manage parking slots.');
      return;
    }

    try {
      const idToken = await getIdToken(msalInstance);
      if (!idToken) {
        throw new Error(t('auth.failedToGetToken'));
      }

      const payload = {
        ...formData,
      };

      let response;
      if (isCreating) {
        // Create new slot
        response = await fetch(getApiUrl('/api/parkingSlots'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify(payload),
        });
      } else if (editingSlot) {
        // Update existing slot
        payload.id = editingSlot.id;
        response = await fetch(getApiUrl('/api/parkingSlots'), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify(payload),
        });
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || t('slotManager.failedToSave', { error: '' }));
      }

      // Reload slots and close form
      await loadSlots();
      handleCancel();
    } catch (e) {
      console.error('Failed to save parking slot:', e);
      alert(t('slotManager.failedToSave', { error: e.message }));
    }
  };

  const handleDelete = async (slot) => {
    if (!canManageSlots) {
      alert(t('slotManager.mustBePowerUser'));
      return;
    }

    const confirmed = window.confirm(
      t('slotManager.deleteSlotConfirm', { label: slot.label })
    );

    if (!confirmed) return;

    try {
      const idToken = await getIdToken(msalInstance);
      if (!idToken) {
        throw new Error(t('auth.failedToGetToken'));
      }

      const response = await fetch(getApiUrl('/api/parkingSlots'), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          id: slot.id,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || t('slotManager.failedToDelete', { error: '' }));
      }

      // Reload slots
      await loadSlots();
    } catch (e) {
      console.error('Failed to delete parking slot:', e);
      alert(t('slotManager.failedToDelete', { error: e.message }));
    }
  };

  if (!canManageSlots) {
    return (
      <div className="slot-manager-container">
        <div className="unauthorized-message">
          <h2>{t('slotManager.powerUserAccessRequired')}</h2>
          <p>{t('slotManager.powerUserAccessMessage')}</p>
          {user?.isPowerUser && (
            <p className="hint">{t('slotManager.enablePowerUserHint')}</p>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="slot-manager-container">
        <div style={{ textAlign: 'center', padding: '2rem' }}>{t('slotManager.loadingSlots')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="slot-manager-container">
        <div style={{ textAlign: 'center', padding: '2rem', color: '#dc2626' }}>{error}</div>
      </div>
    );
  }

  return (
    <div className="slot-manager-container">
      <div className="slot-manager-header">
        <h2>{t('slotManager.title')}</h2>
        <button onClick={handleCreateNew} className="btn-create" disabled={isCreating}>
          {t('slotManager.addNewSlot')}
        </button>
      </div>

      {(isCreating || editingSlot) && (
        <div className="slot-manager-modal-overlay" onClick={handleCancel}>
          <div className="slot-manager-modal" onClick={(e) => e.stopPropagation()}>
            <div className="slot-manager-modal-header">
              <h2>{isCreating ? t('slotManager.createNewSlot') : t('slotManager.editSlot', { label: editingSlot.label })}</h2>
              <button className="slot-manager-modal-close" onClick={handleCancel}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="slot-manager-form">
            <div className="form-group">
              <label htmlFor="label">
                {t('slotManager.slotLabel')} <span className="required">*</span>
              </label>
              <input
                type="text"
                id="label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder={t('slotManager.slotLabelPlaceholder')}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="location">
                {t('slotManager.location')} <span className="required">*</span>
              </label>
              <select
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
              >
                <option value="basement">{t('parkingTable.basement')}</option>
                <option value="ground_floor">{t('parkingTable.groundFloor')}</option>
              </select>
              <small className="form-hint">{t('slotManager.selectLocation')}</small>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.ev_charging}
                  onChange={(e) => setFormData({ ...formData, ev_charging: e.target.checked })}
                />
                <span>{t('slotManager.evCharging')}</span>
              </label>
              <small className="form-hint">
                {t('slotManager.evChargingHint')}
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="default_owner">{t('slotManager.defaultOwner')}</label>
              <input
                type="text"
                id="default_owner"
                value={formData.default_owner}
                onChange={(e) => setFormData({ ...formData, default_owner: e.target.value })}
                placeholder={t('slotManager.defaultOwnerPlaceholder')}
              />
              <small className="form-hint">
                {t('slotManager.defaultOwnerHint')}
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="display_order">{t('slotManager.displayOrder')}</label>
              <input
                type="number"
                id="display_order"
                value={formData.display_order}
                onChange={(e) =>
                  setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })
                }
                placeholder={t('slotManager.displayOrderPlaceholder')}
                min="1"
              />
              <small className="form-hint">
                {t('slotManager.displayOrderHint')}
              </small>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.available}
                  onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                />
                <span>{t('slotManager.availableForReservations')}</span>
              </label>
              <small className="form-hint">
                {t('slotManager.availableHint')}
              </small>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-submit">
                {isCreating ? t('slotManager.createSlot') : t('slotManager.updateSlot')}
              </button>
              <button type="button" onClick={handleCancel} className="btn-cancel">
                {t('common.cancel')}
              </button>
            </div>
          </form>
          </div>
        </div>
      )}

      <div className="slots-list">
        <h3>{t('slotManager.existingSlots', { count: slots.length })}</h3>
        {slots.length === 0 ? (
          <div className="empty-state">
            <p>{t('slotManager.noSlotsConfigured')}</p>
            <p>{t('slotManager.createFirstSlot')}</p>
          </div>
        ) : (
          <div className="slots-table-wrapper">
            <table className="slots-table">
              <thead>
                <tr>
                  <th>{t('slotManager.order')}</th>
                  <th>{t('slotManager.label')}</th>
                  <th>{t('slotManager.locationColumn')}</th>
                  <th>{t('slotManager.evChargingColumn')}</th>
                  <th>{t('slotManager.defaultOwnerColumn')}</th>
                  <th>{t('slotManager.status')}</th>
                  <th>{t('slotManager.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {slots.map((slot) => (
                  <tr key={slot.id}>
                    <td>{slot.display_order}</td>
                    <td>
                      <strong>{slot.label}</strong>
                    </td>
                    <td>
                      <span className="location-badge">
                        {slot.location === 'basement' ? `🏢 ${t('parkingTable.basement')}` : `🏛️ ${t('parkingTable.groundFloor')}`}
                      </span>
                    </td>
                    <td className="text-center">{slot.ev_charging ? '⚡' : '-'}</td>
                    <td>{slot.default_owner || <em className="text-muted">{t('common.none')}</em>}</td>
                    <td>
                      <span
                        className={`status-badge ${slot.available ? 'available' : 'unavailable'}`}
                      >
                        {slot.available ? t('slotManager.availableStatus') : t('slotManager.unavailableStatus')}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(slot)}
                          className="btn-edit"
                          disabled={editingSlot?.id === slot.id}
                        >
                          {t('common.edit')}
                        </button>
                        <button onClick={() => handleDelete(slot)} className="btn-delete">
                          {t('common.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ParkingSlotManager;
